'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { sendPasswordReset } from '@/api';
import { toast } from 'react-hot-toast';
import useUser from '@/hooks/useUser';

const resetPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ForgotPasswordModal({ open, onOpenChange }: ForgotPasswordModalProps) {
  const { user } = useUser();
  const [isSending, setIsSending] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: user?.email || '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setIsSending(true);
    try {
      await sendPasswordReset({ body: { email: values.email } });
      toast.success('Password reset email sent');
      onOpenChange(false);
    } catch (error) {
      toast.error("Couldn't send password reset email");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      disabled={isSending || !!user}
                      readOnly={!!user}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isSending} className="w-full">
              {isSending ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default ForgotPasswordModal;
